import { ManhattanOneContractorSyncRequest, ManhattanOneContractorSyncSchedule } from './service.connection';

// A run takes minutes, so a shorter interval would start the next run the moment the last one ends
export const MANHATTAN_ONE_SYNC_MIN_INTERVAL_MINUTES = 5;

// The worker checks roughly every 30s; a "Sync now" left waiting this long means the worker is not running
export const MANHATTAN_ONE_SYNC_REQUEST_STALE_MS = 2 * 60 * 1000;

export type ManhattanOneSyncIntervalUnit = 'minutes' | 'hours';

export interface ManhattanOneSyncInterval {
    value: number;
    unit: ManhattanOneSyncIntervalUnit;
}

/** When the schedule runs next: null while it is off, now when it has never run */
export function manhattanOneSyncNextRunAt(schedule: ManhattanOneContractorSyncSchedule | null | undefined, lastAttemptAt: Date | string | null | undefined, now = Date.now()): Date | null {

    if (!schedule?.enabled || !(schedule.intervalMinutes >= MANHATTAN_ONE_SYNC_MIN_INTERVAL_MINUTES)) return null;
    if (!lastAttemptAt) return new Date(now);
    return new Date(new Date(lastAttemptAt).getTime() + schedule.intervalMinutes * 60 * 1000);
}

export function isManhattanOneSyncDue(schedule: ManhattanOneContractorSyncSchedule | null | undefined, lastAttemptAt: Date | string | null | undefined, now = Date.now()): boolean {

    const next = manhattanOneSyncNextRunAt(schedule, lastAttemptAt, now);
    return !!next && next.getTime() <= now;
}

/** A "Sync now" recent enough that the worker should still pick it up; another press is refused meanwhile */
export function isManhattanOneSyncRequestPending(request: ManhattanOneContractorSyncRequest | null | undefined, now = Date.now()): boolean {

    return !!request && now - new Date(request.requestedAt).getTime() < MANHATTAN_ONE_SYNC_REQUEST_STALE_MS;
}

/** A "Sync now" nobody has picked up: the worker is down, and pressing again replaces it */
export function isManhattanOneSyncRequestStale(request: ManhattanOneContractorSyncRequest | null | undefined, now = Date.now()): boolean {

    return !!request && !isManhattanOneSyncRequestPending(request, now);
}

/** Whole hours are shown in hours, anything else in minutes */
export function toManhattanOneSyncInterval(minutes: number): ManhattanOneSyncInterval {

    return minutes >= 60 && minutes % 60 === 0
        ? { value: minutes / 60, unit: 'hours' }
        : { value: minutes, unit: 'minutes' };
}

export function fromManhattanOneSyncInterval(interval: ManhattanOneSyncInterval): number {

    return interval.unit === 'hours'
        ? interval.value * 60
        : interval.value;
}

/** "every hour", "every 12 hours", "every 5 minutes" */
export function describeManhattanOneSyncInterval(minutes: number): string {

    const { value, unit } = toManhattanOneSyncInterval(minutes);
    return value === 1
        ? `every ${unit.slice(0, -1)}`
        : `every ${value} ${unit}`;
}
